import { Form, FormikProps, withFormik } from 'formik';
import noop from 'lodash/noop';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { BillingDetailsT } from '../../../../api/payment/payment.d';
import FormikCheckbox from '../../../../components/FormikCheckbox/FormikCheckbox';
import FormikInput from '../../../../components/FormikInput/FormikInput';
import { FieldWrapperStyled } from '../../../../components/FormikInput/FormikInput.styled';
import FormikPhoneInput from '../../../../components/FormikPhoneInput/FormikPhoneInput';
import GuestInformationFormSchema from '../../../../validationSchemas/GuestInformationFormSchema';
import {
  FormRowStyled,
  SectionTitleStyled,
  SectionWrapperStyled,
} from '../../PaymentDetailsPage.styled';

type GuestInformationFormPropsT = {
  isFieldsDisabled: boolean;
};

export type GuestInformationFormValueT = { isContributorAnotherPerson: boolean } & BillingDetailsT;

const DEFAULT_VALUES = {
  firstName: '',
  lastName: '',
  contributorFirstName: '',
  contributorLastName: '',
  address: '',
  city: '',
  state: '',
  country: '',
  postalCode: '',
  email: '',
  phoneNo: '',
  isContributorAnotherPerson: false,
};

const GuestInformationForm: React.FC<
  GuestInformationFormPropsT & FormikProps<GuestInformationFormValueT>
> = ({ values, children, isFieldsDisabled, setFieldValue, setFieldTouched }) => {
  const { isContributorAnotherPerson } = values;

  useEffect(() => {
    setFieldValue('contributorFirstName', '');
    setFieldValue('contributorLastName', '');
    setFieldTouched('contributorFirstName', false);
    setFieldTouched('contributorLastName', false);
  }, [isContributorAnotherPerson]);

  return (
    <>
      <SectionWrapperStyled>
        <SectionTitleStyled level={3}>
          <FormattedMessage id="guestInfo.form.title" defaultMessage="Guest Information" />
        </SectionTitleStyled>

        <Form>
          <FormRowStyled>
            <FormikInput
              name="firstName"
              disabled={isFieldsDisabled}
              label={<FormattedMessage id="form.firstName" defaultMessage="First Name" />}
            />

            <FormikInput
              name="lastName"
              disabled={isFieldsDisabled}
              label={<FormattedMessage id="form.lastName" defaultMessage="Last Name" />}
            />
          </FormRowStyled>

          <FormRowStyled>
            <FormikCheckbox
              name="isContributorAnotherPerson"
              disabled={isFieldsDisabled}
              label={
                <FormattedMessage
                  id="form.contributor.anotherPerson"
                  defaultMessage="Contributor is another person"
                />
              }
            />
          </FormRowStyled>

          {isContributorAnotherPerson && (
            <FormRowStyled>
              <FormikInput
                name="contributorFirstName"
                disabled={isFieldsDisabled}
                label={
                  <FormattedMessage
                    id="form.contributor.firstName"
                    defaultMessage="Contributor first name"
                  />
                }
              />

              <FormikInput
                name="contributorLastName"
                disabled={isFieldsDisabled}
                label={
                  <FormattedMessage
                    id="form.contributor.lastName"
                    defaultMessage="Contributor last name"
                  />
                }
              />
            </FormRowStyled>
          )}

          <FormRowStyled>
            <FormikInput
              name="email"
              disabled={isFieldsDisabled}
              label={<FormattedMessage id="form.emailAddress" defaultMessage="Email Address" />}
            />

            <FormikInput
              name="address"
              disabled={isFieldsDisabled}
              label={<FormattedMessage id="form.address" defaultMessage="Address" />}
            />
          </FormRowStyled>

          <FormRowStyled>
            <FormikInput
              name="city"
              disabled={isFieldsDisabled}
              label={<FormattedMessage id="form.city" defaultMessage="City" />}
            />

            <FormikInput
              isRequired={false}
              disabled={isFieldsDisabled}
              name="state"
              label={<FormattedMessage id="form.state" defaultMessage="State" />}
            />
          </FormRowStyled>

          <FormRowStyled>
            <FormikInput
              name="country"
              disabled={isFieldsDisabled}
              label={<FormattedMessage id="foundItem.country" defaultMessage="Country" />}
            />

            <FormikInput
              name="postalCode"
              disabled={isFieldsDisabled}
              label={<FormattedMessage id="form.postalCode" defaultMessage="Postal Code" />}
            />
          </FormRowStyled>

          <FormRowStyled>
            <FormikPhoneInput
              name="phoneNo"
              disabled={isFieldsDisabled}
              label={<FormattedMessage id="form.phoneNumber" defaultMessage="Phone Number" />}
            />
            <FieldWrapperStyled></FieldWrapperStyled>
          </FormRowStyled>
        </Form>
      </SectionWrapperStyled>

      {children}
    </>
  );
};

export default withFormik<GuestInformationFormPropsT, GuestInformationFormValueT>({
  validationSchema: GuestInformationFormSchema,
  validateOnBlur: false,
  mapPropsToValues: () => DEFAULT_VALUES,
  handleSubmit: () => noop,
})(GuestInformationForm);
